import { ColorNeutralDarkGrey, Spacing16, Spacing24, Spacing4, Spacing8 } from 'autogen/design-tokens/tokens';
import type { ChoosableSubscriptionFrequencyGroup, ProductInformationViewModel } from 'autogen/swagger/all';
import { HorizontalPriceModule } from 'components/generic/productPrice';
import { getSelectedGroup } from 'components/subscription/helpers/subscriptionFrequencyHelper';
import styled, { css } from 'styled-components';
import { CampaignInfoBox } from 'components/features/CampaignInfo';
import PDPHandle from 'components/progressiveDiscount/pdpHandle';
import { useTranslation } from 'react-i18next';
import { UrlText } from 'autogen/translation-keys/trans-website-progressivediscount';
import PageTitleNameRendererMapper from 'components/generic/productName/components/PageTitleNameRendererMapper';
import CombinedProductActions from 'components/product/pdpProductAction/CombinedProductActions';
import { Typography } from 'components/generic/typography';
import CrossSellProducts from 'components/productRecommendation/components/CrossSellProducts';
import { useMapPriceInfoToIPriceModule } from 'components/generic/productPrice/components/shared/helpers';
import { type MouseEvent, useState } from 'react';
import type { TrackingCategory } from 'modules/tracking/UAEventTracking';
import PosStoreStock from 'components/StockInfo/components/StoreStock/PosStoreStock';
import { breakpoint, sizes } from 'variables';
import InlineButton from 'components/generic/InlineButton';
import { productAccordionAnchorId } from 'components/product/accordion/ProductAccordion';
import { useIsChanel } from 'hooks/user/useIsBrand';
import { ProductAccordionAnchor } from 'autogen/translation-keys/trans-website-productreview';
import type { IProductActionButton } from 'components/generic/productCTA/shared/types';
import Points from './components/Points';
import ReviewAnchor from './components/ReviewAnchor';
import SubscriptionPriceInfo from './components/SubscriptionPriceInfo/SubscriptionPriceInfo';
import { getSelectedItem } from './components/SubscriptionPriceInfo/components/components/SubscriptionFrequencySelection/shared/helpers';
import BundleNotice from './components/BundleNotice';
import DeliveryInformationList from './components/DeliveryInformationList';
import GWPOffer from './components/GWPOffer';
import CompetitorMessage from './components/CompetitorMessage';
import CampaignOffer from './components/CampaignOffer';
import { SelectedFrequencyContext, SubscriptionContext } from './hooks/context';
import Variants from './components/Variants/Variants';
import SplashTagLines from './components/SplashTagLines';
import ChanelVariants from './components/Variants/chanel/ChanelVariants';
import useGA4Tracking from 'modules/tracking/GA4';
import ExpectedDelivery from './components/ExpectedDelivery';
import ChanelHorizontalPriceModule from 'components/generic/productPrice/components/ChanelHorizontalPriceModule';
import PointsChanel from './components/PointsChanel';
import StatusItem from 'components/StatusItem';
import StockInfo from 'components/StockInfo';
import WebsaleAvailability from './components/WebsaleAvailability';

// #region Style
const Wrapper = styled.div`
    display: grid;
    grid-auto-columns: auto;
    row-gap: ${Spacing24};
`;

const Top = styled.div`
    display: grid;
    grid-template-columns: auto;
    row-gap: ${Spacing8};
`;

const PriceContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${Spacing4};
`;

const StyledSplashTagLines = styled(SplashTagLines)`
    display: none;

    ${breakpoint.up(sizes.lg)} {
        display: flex;
    }
`;

const AnchorContainer = styled.div`
    display: flex;
    column-gap: ${Spacing16};
    row-gap: ${Spacing8};

    a {
        color: ${ColorNeutralDarkGrey};
    }
`;

const ProductAccordionLink = styled(InlineButton)`
    display: none;

    ${breakpoint.up(sizes.lg)} {
        display: inline-block;
    }
`;

const ActionAndStockContainer = styled.div`
    display: grid;
    grid-template-columns: auto;
    row-gap: ${Spacing16};
`;

const FlexOrderContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing24};
    min-width: 0;
`;

const StyledVariants = styled(Variants)<{ $orderFirst?: boolean }>`
    ${({ $orderFirst }) =>
        $orderFirst
            ? css`
                  order: 0;
              `
            : css`
                  ${breakpoint.up(sizes.lg)} {
                      order: 1;
                  }
              `}
`;

const StyledChanelVariants = styled(ChanelVariants)<{ $orderFirst?: boolean }>`
    ${({ $orderFirst }) =>
        $orderFirst
            ? css`
                  order: 0;
              `
            : css`
                  ${breakpoint.up(sizes.lg)} {
                      order: 1;
                  }
              `}
`;

const StyledGWPOffer = styled(GWPOffer)`
    max-width: 480px;
`;

const StyledCrossSellProducts = styled(CrossSellProducts)`
    max-width: 480px;
`;

// #endregion Style

function getInitialSelectedItem(choosable: ChoosableSubscriptionFrequencyGroup[] | undefined) {
    if (!choosable) return undefined;

    const selectedGroup = getSelectedGroup(choosable);
    const selectedItem = getSelectedItem(selectedGroup?.frequencies || choosable[0].frequencies);

    return selectedItem;
}

export default function MidSection({ model }: { model: ProductInformationViewModel }): JSX.Element {
    const {
        productId,
        externalProductId,
        linkAction,
        name,
        reviews,
        showReviews,
        priceInfo,
        showPossibleAdditionalDeliveryCost,
        campaignInfo,
        variants,
        favoriteStatusModel,
        isDonation,
        activeFromText,
        isWebshopActive,
        isPreorderActive,
        gwpOfferViewModel,
        progressiveDiscountViewModel,
        productPageAction,
        expectedDelivery,
        returnInformationText,
        returnInformationLinkAction,
        crossSellProducts,
        competitorMessage,
        offer,
        isLightPdp = false,
        productButtonContext,
        uaTrackingCategory,
        fromPowerstep,
        addToBasketType,
        customerNotification,
        dataLayerDto,
        gwpOfferDetailsLinkAction,
        posStoreStock,
        splashes,
        makeupInfo,
        revieveVirtualTryOn,
        findationViewModel,
        webSaleAvailabilityStatus
    } = model;
    const { pointInfo, minimumSalesQuantity, isBundle } = priceInfo;
    const { subscriptionPriceInfoViewModel, storeStock, stockStatus } = expectedDelivery || {};
    const {
        isSubscriptionSelected = false,
        subscriptionInfoModel,
        subscriptionPriceInfo
    } = subscriptionPriceInfoViewModel || {};
    const { discountId: subscriptionDiscountId, subscriptionMinimumQuantity: subscriptionMinimumSalesQuantity } =
        subscriptionPriceInfo || {};
    const productActionModel: Omit<IProductActionButton['model'], 'action'> = {
        productId,
        externalProductId,
        productNavigationLink: linkAction,
        minimumSalesQuantity,
        subscriptionMinimumSalesQuantity,
        listName: dataLayerDto.listName,
        fromPowerstep,
        showPowerstepOnAddToBasket: true,
        addToBasketType,
        customerNotification,
        trackingContext: {
            type: productButtonContext,
            eventCategory: uaTrackingCategory as TrackingCategory // TODO don't get category from BE
        },
        subscriptionDiscountId
    };
    const { t } = useTranslation();
    const [subscriptionActive, setSubscriptionActive] = useState(isSubscriptionSelected);
    const [selectedFrequency, setSelectedFrequency] = useState(
        getInitialSelectedItem(subscriptionInfoModel?.frequencyInfo.choosable)
    );
    const formattedPriceInfo = useMapPriceInfoToIPriceModule(priceInfo, showPossibleAdditionalDeliveryCost);
    const isChanel = useIsChanel();
    const GA4Tracking = useGA4Tracking();

    function aboutProductAnchorClick(e: MouseEvent) {
        e.preventDefault();
        document.getElementById(`${productAccordionAnchorId}`)?.scrollIntoView({
            behavior: 'smooth'
        });
        GA4Tracking({
            eventName: 'link_click',
            category: 'ecommerce',
            eventParams: [
                { param_name: 'link_type', param_value: 'internal' },
                { param_name: 'link_sub_type', param_value: 'anchor' },
                { param_name: 'link_text', param_value: `${t(ProductAccordionAnchor)}` }
            ]
        });
    }

    return (
        <SubscriptionContext.Provider value={{ subscriptionActive, setSubscriptionActive }}>
            <SelectedFrequencyContext.Provider value={{ selectedFrequency, setSelectedFrequency }}>
                <Wrapper>
                    <Top>
                        <StyledSplashTagLines splashes={splashes} />

                        <PageTitleNameRendererMapper lines={name.lines} />

                        {!isChanel && (
                            <AnchorContainer>
                                {reviews && showReviews && <ReviewAnchor reviews={reviews} />}
                                <ProductAccordionLink
                                    forwardedAs="a"
                                    href={`#${productAccordionAnchorId}`}
                                    size={{ xs: 'xs', lg: 'sm' }}
                                    onClick={(e) => {
                                        aboutProductAnchorClick(e);
                                    }}
                                >
                                    {t(ProductAccordionAnchor)}
                                </ProductAccordionLink>
                            </AnchorContainer>
                        )}
                    </Top>

                    {isWebshopActive ? (
                        <>
                            <FlexOrderContainer>
                                {variants &&
                                    (isChanel ? (
                                        <StyledChanelVariants
                                            variants={variants}
                                            productId={productId}
                                            $orderFirst={!!subscriptionPriceInfoViewModel}
                                        />
                                    ) : (
                                        <StyledVariants
                                            variants={variants}
                                            productId={productId}
                                            externalProductId={externalProductId}
                                            name={name}
                                            makeupInfo={makeupInfo}
                                            revieveVirtualTryOn={revieveVirtualTryOn}
                                            $orderFirst={!!subscriptionPriceInfoViewModel}
                                            findationViewModel={findationViewModel}
                                        />
                                    ))}

                                <PriceContainer>
                                    {offer && <CampaignOffer campaignOffer={offer} />}

                                    {subscriptionPriceInfoViewModel ? (
                                        <SubscriptionPriceInfo model={subscriptionPriceInfoViewModel} />
                                    ) : isChanel ? (
                                        <ChanelHorizontalPriceModule {...{ ...formattedPriceInfo, size: 24 }} />
                                    ) : (
                                        <HorizontalPriceModule {...{ ...formattedPriceInfo, size: 24 }} />
                                    )}

                                    {competitorMessage && <CompetitorMessage competitorMessage={competitorMessage} />}

                                    {!isPreorderActive &&
                                        !customerNotification &&
                                        pointInfo &&
                                        (isChanel ? (
                                            <PointsChanel
                                                points={pointInfo}
                                                overwritePoints={
                                                    subscriptionPriceInfoViewModel?.subscriptionPriceInfo.pointInfo
                                                }
                                            />
                                        ) : (
                                            <Points
                                                points={pointInfo}
                                                overwritePoints={
                                                    subscriptionPriceInfoViewModel?.subscriptionPriceInfo.pointInfo
                                                }
                                            />
                                        ))}
                                </PriceContainer>

                                {campaignInfo && <CampaignInfoBox campaignInfo={campaignInfo} productId={productId} />}

                                {progressiveDiscountViewModel && (
                                    <PDPHandle
                                        model={progressiveDiscountViewModel}
                                        translations={{ seeOffer: t(UrlText) }}
                                    />
                                )}
                            </FlexOrderContainer>

                            <ActionAndStockContainer>
                                {isBundle && <BundleNotice minimumSalesQuantity={minimumSalesQuantity} />}

                                {webSaleAvailabilityStatus && (
                                    <WebsaleAvailability availability={webSaleAvailabilityStatus} />
                                )}

                                <CombinedProductActions
                                    action={productPageAction}
                                    productActionModel={productActionModel}
                                    favoriteModel={favoriteStatusModel}
                                />

                                {posStoreStock && <PosStoreStock model={posStoreStock} />}

                                {!isLightPdp && (
                                    <>
                                        <StockInfo
                                            storeStock={storeStock}
                                            productStock={stockStatus}
                                            productId={productId}
                                        />

                                        {expectedDelivery && <ExpectedDelivery {...model} />}

                                        <DeliveryInformationList
                                            returnInformationText={returnInformationText}
                                            returnInformationLinkAction={returnInformationLinkAction}
                                        />
                                    </>
                                )}
                            </ActionAndStockContainer>

                            {gwpOfferViewModel && (
                                <StyledGWPOffer
                                    gwpOfferModel={gwpOfferViewModel}
                                    gwpOfferDetailsLinkAction={gwpOfferDetailsLinkAction}
                                />
                            )}

                            {crossSellProducts && crossSellProducts?.length > 0 && (
                                <StyledCrossSellProducts items={crossSellProducts} />
                            )}
                        </>
                    ) : (
                        <>
                            {activeFromText && (
                                <Typography.Body size="M" strong>
                                    {activeFromText}
                                </Typography.Body>
                            )}
                        </>
                    )}
                </Wrapper>
            </SelectedFrequencyContext.Provider>
        </SubscriptionContext.Provider>
    );
}
